import React, {useCallback, useMemo, useState, useEffect} from 'react';
import {GatewayGetSchema} from '../../../../../api/DBSchema/Gateway';
import {SMCCheckbox} from '../../../../../components/Simple/SMCCheckbox';
import {useTranslation} from 'react-i18next';
import SMCLabel from '../../../../../components/Simple/SMCLabel';
import EditorLayout from '../../../../../components/Layouts/EditorLayout';
import EmptyRow from '../../../../../components/Simple/EmptyRow';
import {SMCRadioGroup, SMCRadio} from '../../../../../components/Simple/SMCRadio';
import Indentation from '../../../../../components/Simple/Indentation';
import {SMCSelect, SMCOption} from '../../../../../components/Simple/SMCSelect';
import RowLayout from '../../../../../components/Layouts/RowLayout';
import SMCTextInput from '../../../../../components/Simple/SMCTextInput';
import SMCPasswordInput from '../../../../../components/Simple/SMCPasswordInput';

const pageSchema = GatewayGetSchema.pages.serviceBackup;
const pageId = pageSchema.pageId;

function BasePeriodicBackup({
    pageData,
    setObjectData,
    pageDisabled
}) {
    const { t } = useTranslation();
    
    const setPageValue = useCallback((param, newValue) => {
        setObjectData([pageId, param], newValue);
    }, [setObjectData]);

    const protocol                  = pageData[pageSchema.protocol];
    const backupServerPath          = pageData[pageSchema.backupServerPath];
    const username                  = pageData[pageSchema.username];
    const password                  = pageData[pageSchema.password];
    const useFileEncryption         = pageData[pageSchema.useFileEncryption];
    const passwordForEncrypt        = pageData[pageSchema.passwordForEncrypt];
    const frequency                 = pageData[pageSchema.frequency];
    const dayOfMonth                = pageData[pageSchema.dayOfMonth];
    const dayOfWeek                 = pageData[pageSchema.dayOfWeek];
    const backupTime                = pageData[pageSchema.backupTime];

    const frequencyValues           = pageSchema.frequencyValues;
    const dayOfWeekValues           = pageSchema.dayOfWeekValues;

    const summaryProtocolsValues = pageSchema.summaryProtocolsValues;

    const [useFlash, setUseFlash] = useState(false);

    useEffect(() => {
        if(protocol=== 'flash'){
            setUseFlash(true);
        }
    }, [protocol]);

    // Options of 00:00-01:00 until 23:00-00:00
    const backupTimeOptions = useMemo(() => (
        [...Array(24)].map((e,i) => (
            <SMCOption key={i} value={i}>{i<10?'0':''}{i}:00-{((i+1)%24)<10?'0':''}{(i+1)%24}:00</SMCOption>
        ))
    ), []);

    const dayOfWeekOptions = useMemo(() => (
        <>
        <SMCOption value={dayOfWeekValues.monday}>{t('SMP.MONDAY')}</SMCOption>
        <SMCOption value={dayOfWeekValues.tuesday}>{t('SMP.TUESDAY')}</SMCOption>
        <SMCOption value={dayOfWeekValues.wednesday}>{t('SMP.WEDNESDAY')}</SMCOption>
        <SMCOption value={dayOfWeekValues.thursday}>{t('SMP.THURSDAY')}</SMCOption>
        <SMCOption value={dayOfWeekValues.friday}>{t('SMP.FRIDAY')}</SMCOption>
        <SMCOption value={dayOfWeekValues.saturday}>{t('SMP.SATURDAY')}</SMCOption>
        <SMCOption value={dayOfWeekValues.sunday}>{t('SMP.SUNDAY')}</SMCOption>
        </>
    ), [dayOfWeekValues, t]);

    const dayOfMonthOptions = useMemo(() => (
        [...Array(31)].map((e,i) => (
            <SMCOption key={i+1} value={i+1}>{i+1}</SMCOption>
        ))
    ), [])

    return (
        <EditorLayout>
            <SMCLabel bold>{t('SMP.PLAN.SERVICES.PB.FILE_STORAGE')}</SMCLabel>
            <Indentation>

                <SMCSelect
                    label={t('SMP.PLAN.SERVICES.PB.PROTOCOL')}
                    value={protocol}
                    onChange={e => {setPageValue(pageSchema.protocol, e.target.value); setUseFlash(e.target.value === 'flash')}}
                    tooltip={protocol === 'tftp'? "" : protocol === 'sftp'? t('SMP.PERIODIC_BACKUP_R80_20'): t('SMP.PERIODIC_BACKUP_R81_10_COMING_SOON')}
                    disabled={pageDisabled}
                >
                    <SMCOption value={summaryProtocolsValues.tftp}>{t('SMP.PLAN.SERVICES.PB.PROTOCOL.TFTP')}</SMCOption>
                    <SMCOption value={summaryProtocolsValues.sftp}>{t('SMP.PLAN.SERVICES.PB.PROTOCOL.SFTP')}</SMCOption>
                    <SMCOption value={summaryProtocolsValues.scp}>{t('SMP.PLAN.SERVICES.PB.PROTOCOL.SCP')}</SMCOption>
                    <SMCOption value={summaryProtocolsValues.flash}>{t('SMP.PLAN.SERVICES.PB.PROTOCOL.FLASH')}</SMCOption>
                </SMCSelect>
                <EmptyRow />
                {/* Path */}
                <RowLayout>
                    <SMCLabel useContextWidth inline>{t('SMP.PLAN.SERVICES.PB.PATH')}</SMCLabel>
                    <SMCTextInput
                         disabled={pageDisabled || useFlash}
                         value={useFlash ? "" : backupServerPath}
                        onChange={e => setPageValue(pageSchema.backupServerPath, e.target.value)}
                    />
                </RowLayout>
                {/* Username */}
                <SMCTextInput
                    label={t('SMP.PLAN.SERVICES.PB.USER_NAME')}
                    disabled={pageDisabled || useFlash}
                    value={useFlash ? "" : username}
                    onChange={e => setPageValue(pageSchema.username, e.target.value)}
                />
                {/* Password */}
                <SMCPasswordInput
                    label={t('SMP.PLAN.SERVICES.PB.PASSWORD')}
                    disabled={pageDisabled || useFlash}
                    value={useFlash ? "" : password}
                    onChange={e => setPageValue(pageSchema.password, e.target.value)}
                    allowPasswordPeek={true}
                />
            </Indentation>
            
            <EmptyRow />

            <SMCLabel><b>{t('SMP.PLAN.SERVICES.PB.NOTE')}: </b>{t('SMP.PLAN.SERVICES.PB.LINE1')}</SMCLabel>

            <EmptyRow />

            <SMCLabel bold>{t('SMP.PLAN.SERVICES.PB.FILE_ENCRYPTION')}</SMCLabel>
            <Indentation>
                <SMCCheckbox
                    disabled={pageDisabled}
                    checked={useFileEncryption}
                    onChange={e => setPageValue(pageSchema.useFileEncryption, e.target.checked)}
                    label={t('SMP.PLAN.SERVICES.PB.USE_FILE_ENCRYPTION')}
                />
                <SMCPasswordInput
                    label={t('SMP.PLAN.SERVICES.PB.PASSWORD')}
                    disabled={pageDisabled || !useFileEncryption}
                    value={passwordForEncrypt}
                    onChange={e => setPageValue(pageSchema.passwordForEncrypt, e.target.value)}
                    allowPasswordPeek={true}
                />
            </Indentation>

            <EmptyRow />

            <SMCLabel bold>{t('SMP.PLAN.SERVICES.PB.SCHEDULE_PB')}</SMCLabel>
            <Indentation>
                <SMCRadioGroup
                    value={frequency}
                    onChange={e => setPageValue(pageSchema.frequency, e.target.value)}
                    disabled={pageDisabled}
                >
                    <SMCRadio value={frequencyValues.daily} label={t('SMP.DAILY')} />
                    {
                        frequency === frequencyValues.daily && (
                            <Indentation>
                                <SMCSelect label={t('SMP.PLAN.SERVICES.FIRMWARE.TIME_OF_DAY')} value={backupTime} onChange={e => setPageValue(pageSchema.backupTime, parseInt(e.target.value))}>
                                    { backupTimeOptions }
                                </SMCSelect>
                            </Indentation>
                        )
                    }
                    <SMCRadio value={frequencyValues.weekly} label={t('SMP.WEEKLY')} />
                    {
                        frequency === frequencyValues.weekly && (
                            <Indentation>
                                <SMCSelect
                                    label={t('SMP.PLAN.SERVICES.FIRMWARE.DAY_OF_WEEK')}
                                    value={dayOfWeek}
                                    onChange={e => setPageValue(pageSchema.dayOfWeek, e.target.value)}
                                    disabled={pageDisabled}
                                >
                                    { dayOfWeekOptions }
                                </SMCSelect>
                                <SMCSelect
                                    label={t('SMP.PLAN.SERVICES.FIRMWARE.TIME_OF_DAY')}
                                    value={backupTime}
                                    onChange={e => setPageValue(pageSchema.backupTime, parseInt(e.target.value))}
                                    disabled={pageDisabled}
                                >
                                    { backupTimeOptions }
                                </SMCSelect>
                            </Indentation>
                        )
                    }
                    <SMCRadio value={frequencyValues.monthly} label={t('SMP.MONTHLY')} />
                    {
                        frequency === frequencyValues.monthly && (
                            <Indentation>
                                <SMCSelect label={t('SMP.PLAN.SERVICES.FIRMWARE.DAY_OF_MONTH')} value={dayOfMonth} onChange={e => setPageValue(pageSchema.dayOfMonth, e.target.value)}>
                                    { dayOfMonthOptions }
                                </SMCSelect>
                                <SMCSelect label={t('SMP.PLAN.SERVICES.FIRMWARE.TIME_OF_DAY')} value={backupTime} onChange={e => setPageValue(pageSchema.backupTime, parseInt(e.target.value))}>
                                    { backupTimeOptions }
                                </SMCSelect>
                            </Indentation>
                        )
                    }
                </SMCRadioGroup>
            </Indentation>
        </EditorLayout>
    );
}

export default BasePeriodicBackup;